import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

const TOKEN_KEY = 'token';
const TOKEN_TIME_KEY = 'token_time';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

export const AuthProvider = ({ children }) => {
  const [token, setTokenState] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    const storedTime = localStorage.getItem(TOKEN_TIME_KEY);
    if (stored && storedTime) {
      setTokenState(stored);
      // Fetch user data from the stored token
      fetchUserData(stored);
    }
  }, []);

  const setToken = useCallback((newToken) => {
    if (newToken) {
      setTokenState(newToken);
      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(TOKEN_TIME_KEY, Date.now().toString());
    } else {
      setTokenState(null);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_TIME_KEY);
    }
  }, []);

  const isAuthenticated = useCallback(() => {
    const stored = token || localStorage.getItem(TOKEN_KEY);
    const storedTime = localStorage.getItem(TOKEN_TIME_KEY);
    if (!stored || !storedTime) return false;
    const age = Date.now() - parseInt(storedTime, 10);
    return age < MAX_AGE_MS;
  }, [token]);

  const login = (jwt) => {
    setToken(jwt);
    fetchUserData(jwt);
  };
  
  const logout = () => {
    setToken(null);
    setUser(null);
  };
  
  const setUserData = (userData) => setUser(userData);
  
  const fetchUserData = async (token) => {
    try {
      // Decode JWT token to get user information
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userData = {
        username: payload.username || payload.sub || 'user',
        roles: payload.roles || 'USER'
      };
      setUser(userData);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      // Fallback: create a default user object
      const userData = {
        username: 'user',
        roles: 'USER'
      };
      setUser(userData);
    }
  };
  
  const hasRole = (requiredRole) => {
    return user && user.roles.includes(requiredRole);
  };

  return (
    <AuthContext.Provider value={{ token, setToken, isAuthenticated, login, logout, user, setUserData, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 